import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Bell,
  Plus,
  Search,
  Eye,
  Pin,
  AlertTriangle,
  Megaphone,
  Calendar,
  Users,
  GraduationCap,
  BookOpen,
  Loader2,
  MoreHorizontal,
  Edit,
  Trash2,
  Archive,
  Send,
  Clock,
  CheckCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priorityConfig = {
  dusuk: { label: 'Düşük', color: 'bg-slate-100 text-slate-700', icon: null },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-700', icon: null },
  yuksek: { label: 'Yüksek', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  acil: { label: 'Acil', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

const categoryConfig = {
  genel: { label: 'Genel', icon: Megaphone },
  akademik: { label: 'Akademik', icon: BookOpen },
  etkinlik: { label: 'Etkinlik', icon: Calendar },
  politika: { label: 'Politika', icon: Users },
  egitim: { label: 'Eğitim', icon: GraduationCap },
  acil: { label: 'Acil', icon: AlertTriangle },
};

const targetConfig = {
  herkes: 'Herkes',
  ogretmenler: 'Öğretmenler',
  koclar: 'Okuma Koçları',
  uzmanlar: 'Uzmanlar',
  veliler: 'Veliler',
  belirli_siniflar: 'Belirli Sınıflar',
};

const AnnouncementsPage = () => {
  const { user, hasRole } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    summary: '',
    category: 'genel',
    priority: 'normal',
    target_audience: 'herkes',
    is_pinned: false,
  });

  const canManage = hasRole(['tenant_owner', 'center_director', 'reading_coach']);

  const fetchAnnouncements = async () => {
    try {
      const params = new URLSearchParams();
      if (activeTab === 'archived') params.append('include_archived', 'true');
      
      const response = await fetch(`${API_URL}/api/announcements?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        let data = await response.json();
        
        // Filter based on tab
        if (activeTab === 'unread') {
          data = data.filter(a => !a.is_read);
        } else if (activeTab === 'pinned') {
          data = data.filter(a => a.is_pinned);
        } else if (activeTab === 'archived') {
          data = data.filter(a => a.status === 'arsiv');
        } else if (activeTab !== 'all') {
          data = data.filter(a => a.status !== 'arsiv');
        }
        
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Duyurular yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/announcements/unread-count`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    fetchUnreadCount();
  }, [activeTab]);

  const handleView = async (announcement) => {
    try {
      const response = await fetch(`${API_URL}/api/announcements/${announcement.announcement_id}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedAnnouncement(data);
        // Update local state
        setAnnouncements(prev => prev.map(a => 
          a.announcement_id === announcement.announcement_id ? { ...a, is_read: true } : a
        ));
        fetchUnreadCount();
      }
    } catch (error) {
      toast.error('Duyuru yüklenemedi');
    }
  };

  const handleCreate = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim()) {
      toast.error('Başlık ve içerik zorunludur');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/announcements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newAnnouncement),
      });

      if (response.ok) {
        toast.success('Duyuru oluşturuldu');
        setShowCreateDialog(false);
        setNewAnnouncement({
          title: '',
          content: '',
          summary: '',
          category: 'genel',
          priority: 'normal',
          target_audience: 'herkes',
          is_pinned: false,
        });
        fetchAnnouncements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Duyuru oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setCreateLoading(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/announcements/${id}/publish`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Duyuru yayınlandı');
        fetchAnnouncements();
      }
    } catch (error) {
      toast.error('Yayınlama başarısız');
    }
  };

  const handleArchive = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/announcements/${id}/archive`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Duyuru arşivlendi');
        fetchAnnouncements();
        setSelectedAnnouncement(null);
      }
    } catch (error) {
      toast.error('Arşivleme başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu duyuruyu silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/announcements/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Duyuru silindi');
        fetchAnnouncements();
        setSelectedAnnouncement(null);
      }
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const filteredAnnouncements = announcements.filter(a =>
    a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const AnnouncementCard = ({ announcement }) => {
    const priority = priorityConfig[announcement.priority] || priorityConfig.normal;
    const category = categoryConfig[announcement.category] || categoryConfig.genel;
    const CategoryIcon = category.icon;

    return (
      <Card 
        className={`cursor-pointer transition-all hover:shadow-md ${
          !announcement.is_read ? 'border-l-4 border-l-primary bg-primary/5' : ''
        } ${announcement.is_pinned ? 'border-t-4 border-t-secondary' : ''}`}
        onClick={() => handleView(announcement)}
        data-testid={`announcement-card-${announcement.announcement_id}`}
      >
        <CardContent className="pt-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              <div className={`p-2 rounded-lg ${priority.color.split(' ')[0]}`}>
                <CategoryIcon className={`h-5 w-5 ${priority.color.split(' ')[1]}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {announcement.is_pinned && (
                    <Pin className="h-4 w-4 text-secondary fill-secondary" />
                  )}
                  <h3 className="font-semibold text-primary-900 line-clamp-1">
                    {announcement.title}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {announcement.summary || announcement.content.substring(0, 100)}
                </p>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <Badge className={priority.color} variant="secondary">
                    {priority.label}
                  </Badge>
                  <Badge variant="outline">{targetConfig[announcement.target_audience]}</Badge>
                  <span className="flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    {announcement.view_count}
                  </span>
                </div>
              </div>
            </div>
            {canManage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {announcement.status === 'taslak' && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handlePublish(announcement.announcement_id); }}>
                      <Send className="mr-2 h-4 w-4" />
                      Yayınla
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleArchive(announcement.announcement_id); }}>
                    <Archive className="mr-2 h-4 w-4" />
                    Arşivle
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="text-red-600"
                    onClick={(e) => { e.stopPropagation(); handleDelete(announcement.announcement_id); }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Sil
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-muted-foreground">
            <span>{announcement.author_name}</span>
            <span>{new Date(announcement.created_at).toLocaleDateString('tr-TR')}</span>
          </div>
        </CardContent>
      </Card>
    );
  };

  const AnnouncementDetailDialog = () => {
    if (!selectedAnnouncement) return null;
    
    const priority = priorityConfig[selectedAnnouncement.priority] || priorityConfig.normal;
    const category = categoryConfig[selectedAnnouncement.category] || categoryConfig.genel;

    return (
      <Dialog open={!!selectedAnnouncement} onOpenChange={() => setSelectedAnnouncement(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <Badge className={priority.color}>{priority.label}</Badge>
              <Badge variant="outline">{category.label}</Badge>
              {selectedAnnouncement.is_pinned && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Pin className="h-3 w-3" />
                  Sabitlenmiş
                </Badge>
              )}
            </div>
            <DialogTitle className="text-xl">{selectedAnnouncement.title}</DialogTitle>
            <DialogDescription className="flex items-center gap-4 text-sm">
              <span>{selectedAnnouncement.author_name}</span>
              <span>•</span>
              <span>{new Date(selectedAnnouncement.created_at).toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {selectedAnnouncement.view_count} görüntülenme
              </span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="prose prose-sm max-w-none whitespace-pre-wrap">
              {selectedAnnouncement.content}
            </div>
          </div>

          {canManage && (
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => handleArchive(selectedAnnouncement.announcement_id)}>
                <Archive className="h-4 w-4 mr-2" />
                Arşivle
              </Button>
              <Button variant="destructive" onClick={() => handleDelete(selectedAnnouncement.announcement_id)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Sil
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="announcements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900 flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Duyurular
          </h1>
          <p className="text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} okunmamış duyuru` : 'Tüm duyurular okundu'}
          </p>
        </div>
        {canManage && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-announcement-btn">
                <Plus className="h-4 w-4 mr-2" />
                Yeni Duyuru
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Yeni Duyuru Oluştur</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="title">Başlık *</Label>
                  <Input
                    id="title"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                    placeholder="Duyuru başlığı"
                  />
                </div>
                <div>
                  <Label htmlFor="summary">Özet</Label>
                  <Input
                    id="summary"
                    value={newAnnouncement.summary}
                    onChange={(e) => setNewAnnouncement({...newAnnouncement, summary: e.target.value})}
                    placeholder="Kısa özet (opsiyonel)"
                  />
                </div>
                <div>
                  <Label htmlFor="content">İçerik *</Label>
                  <Textarea
                    id="content"
                    value={newAnnouncement.content}
                    onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})}
                    placeholder="Duyuru içeriği"
                    rows={6}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Kategori</Label>
                    <Select
                      value={newAnnouncement.category}
                      onValueChange={(value) => setNewAnnouncement({...newAnnouncement, category: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categoryConfig).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Öncelik</Label>
                    <Select
                      value={newAnnouncement.priority}
                      onValueChange={(value) => setNewAnnouncement({...newAnnouncement, priority: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(priorityConfig).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Hedef Kitle</Label>
                  <Select
                    value={newAnnouncement.target_audience}
                    onValueChange={(value) => setNewAnnouncement({...newAnnouncement, target_audience: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(targetConfig).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_pinned"
                    checked={newAnnouncement.is_pinned}
                    onChange={(e) => setNewAnnouncement({...newAnnouncement, is_pinned: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="is_pinned" className="cursor-pointer">
                    Bu duyuruyu sabitle (en üstte göster)
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  İptal
                </Button>
                <Button onClick={handleCreate} disabled={createLoading}>
                  {createLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Oluştur ve Yayınla
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Duyuru ara..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">Tümü</TabsTrigger>
          <TabsTrigger value="unread" className="flex items-center gap-1">
            Okunmamış
            {unreadCount > 0 && (
              <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center">
                {unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pinned">Sabitlenmiş</TabsTrigger>
          {canManage && <TabsTrigger value="archived">Arşiv</TabsTrigger>}
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {filteredAnnouncements.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {activeTab === 'unread' 
                  ? 'Okunmamış duyuru yok'
                  : activeTab === 'pinned'
                    ? 'Sabitlenmiş duyuru yok'
                    : 'Duyuru bulunamadı'
                }
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredAnnouncements.map(announcement => (
                <AnnouncementCard key={announcement.announcement_id} announcement={announcement} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AnnouncementDetailDialog />
    </div>
  );
};

export default AnnouncementsPage;
